import type { QueryState } from "../../api/useQuery.js";
import { useT } from "@mmm/ui";
interface LoadingSkeletonProps {
  height?: number;
  width?: string;
  className?: string;
}

export function LoadingSkeleton({ height = 14, width = "100%", className = "" }: LoadingSkeletonProps) {
  return <span className={`mmm-skeleton ${className}`.trim()} style={{ height, width }} aria-hidden="true" />;
}

export function LoadingRows({ rows = 3 }: { rows?: number }) {
  const t = useT();
  return (
    <div className="mmm-loading-rows" role="status" aria-label={t("common.loading")}>
      {Array.from({ length: rows }, (_, index) => (
        <div className="mmm-loading-row" key={index}>
          <LoadingSkeleton height={10} width="10px" className="mmm-skeleton--dot" />
          <LoadingSkeleton height={14} width={index % 2 === 0 ? "62%" : "48%"} />
          <LoadingSkeleton height={18} width="72px" />
        </div>
      ))}
    </div>
  );
}

export function LoadingCards({ cards = 3 }: { cards?: number }) {
  const t = useT();
  return (
    <div className="mmm-grid-cards mmm-grid-cards--responsive" role="status" aria-label={t("common.loading")}>
      {Array.from({ length: cards }, (_, index) => (
        <div className="mmm-card mmm-loading-card" key={index}>
          <LoadingSkeleton height={26} width="26px" className="mmm-skeleton--swatch" />
          <LoadingSkeleton height={17} width={index % 2 === 0 ? "68%" : "54%"} />
          <LoadingSkeleton height={24} width="82px" />
        </div>
      ))}
    </div>
  );
}

export function LoadingCalendar() {
  const t = useT();
  return (
    <div className="mmm-loading-calendar" role="status" aria-label={t("common.loadingCalendar")}>
      {Array.from({ length: 35 }, (_, index) => (
        <div className="mmm-loading-calendar-cell" key={index}>
          <LoadingSkeleton height={10} width="18px" />
          {index % 5 === 0 && <LoadingSkeleton height={13} width="76%" />}
          {index % 7 === 0 && <LoadingSkeleton height={13} width="54%" />}
        </div>
      ))}
    </div>
  );
}

export function QueryStatus({ queries }: { queries: Array<QueryState<unknown>> }) {
  const t = useT();
  const waiting = queries.some((query) => query.waiting);
  const refreshing = queries.some((query) => query.refreshing);
  if (!waiting && !refreshing) return null;

  return (
    <div className="mmm-query-status" role="status">
      <span className="mmm-query-status-pulse" aria-hidden="true" />
      <span>{waiting ? t("splash.waitingBackend") : t("common.refreshing")}</span>
    </div>
  );
}
